import {
  Box,
  Button,
  Grid,
  StandardTextFieldProps,
  TextField,
  Typography,
} from '@mui/material';
import {t} from 'i18next';
import React, {forwardRef, useEffect, useRef, useState} from 'react';
import {isEmpty} from '../../../utils/utils';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import AurButton from '../aur-button/AurButton';
import {TruncatedList} from 'react-truncate-list';

interface Props extends StandardTextFieldProps {
  hasSearchesPerm?: boolean;
  selectedRelationshipGroupArray?: string[];
  onPressBuildQuery?: () => void;
  onPressAddRelationshipGroup?: () => void;
  savedSearchScopeTextArea?: string;
  onClickRunSearch: () => void;
  onClickClearAllButton: () => void;
  runDisabled: boolean;
  clearAllDisabled: boolean;
  onHeightChange?: () => void;
}

const AurAdhocSearchTextArea = forwardRef<HTMLElement, Props>((props, ref) => {
  const {
    placeholder = t(
      'indexSearchPage.aurAdhocSearchControl.typeOrPasteSearchTerm'
    ),
    selectedRelationshipGroupArray = [],
    onPressBuildQuery,
    onPressAddRelationshipGroup,
    savedSearchScopeTextArea,
    onClickRunSearch,
    onClickClearAllButton,
    onHeightChange,
    runDisabled,
    clearAllDisabled,
    hasSearchesPerm,
    ...rest
  } = props;

  const typographyRef = useRef<HTMLDivElement>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const [isQueryTextfieldFocused, setIsQueryTextfieldFocused] = useState(false);

  const {globalSelectedScopeItem} = useSelector(
    (state: RootState) => state.ecaAdhocSearch
  );

  const [containerWidth, setContainerWidth] = useState<number>(0);

  // Notify parent of height changes
  useEffect(() => {
    if (!textFieldRef.current || !onHeightChange) return;
    const resizeObserver = new ResizeObserver(() => {
      onHeightChange();
    });
    resizeObserver.observe(textFieldRef.current);
    return () => resizeObserver.disconnect();
  }, [onHeightChange]);

  useEffect(() => {
    const container = (ref as React.RefObject<HTMLDivElement>)?.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [ref]);

  const savedSearchScope = savedSearchScopeTextArea;

  return (
    <Box ref={ref} className="aurAdhocSearch-main">
      <Box className="searching-in-heading-container">
        <Box className="searching-in-heading-title">
          {globalSelectedScopeItem?.id || savedSearchScope ? (
            <Box className="scope-container">
              <AurIcon
                icon={IconNames.ScopeIcon}
                color={IconColors.Custom}
                customClassName={'scope-icon'}
              />
              <Typography className="typo-text subhead4_size scope-text" tabIndex={0}>
                {t('indexSearchPage.aurAdhocSearchControl.scope')}
              </Typography>
            </Box>
          ) : null}
          {globalSelectedScopeItem?.id || savedSearchScope ? (
            <Typography className="typo-text body2_size searching-in-heading" tabIndex={0}>
              {(globalSelectedScopeItem?.id &&
                (globalSelectedScopeItem?.name ||
                  globalSelectedScopeItem?.label)) ||
                savedSearchScope}
            </Typography>
          ) : (
            <Typography className="typo-text body2_size searching-in-heading" tabIndex={0}>
              {t('indexSearchPage.aurAdhocSearchControl.searchingIn')}{' '}
              {t('indexSearchPage.aurAdhocSearchControl.allDocuments')}
            </Typography>
          )}
        </Box>
        {hasSearchesPerm && (
          <Button
            data-testid={'buildQueryButton'}
            className={'aurAdhocSearch-button'}
            startIcon={<AurIcon icon={IconNames.WidgetOutlined} />}
            onClick={onPressBuildQuery}
            size={'medium'}
          >
            <Typography className={'aurAdhocSearch-buttonText'} tabIndex={0}>
              {t('indexSearchPage.aurAdhocSearchControl.buildWithWizard')}
            </Typography>
          </Button>
        )}
      </Box>
      <Box className={'aurAdhocSearch-textarea-container'}>
        <Box
          className={'aurAdhocSearch-textarea-background'}
          sx={{borderStyle: isQueryTextfieldFocused ? 'solid' : 'none'}}
        />
        <TextField
          inputRef={textFieldRef}
          onChange={() => {
            if (onHeightChange) setTimeout(() => onHeightChange(), 0);
          }}
          onFocus={() => setIsQueryTextfieldFocused(true)}
          onBlur={() => setIsQueryTextfieldFocused(false)}
          variant={'standard'}
          multiline
          className={'aurAdhocSearch-textfield'}
          minRows={1}
          maxRows={12}
          placeholder={placeholder}
          margin={'none'}
          inputProps={{
            'data-testid': 'queryTextField',
            style: {
              fontFamily: 'Reckless',
              fontFeatureSettings:
                '"liga" 0, "clig" 0, "calt" 0, "dlig" 0, "ss01" 0, "ss02" 0',
              fontVariantNumeric: 'lining-nums',
            },
          }}
          InputProps={{disableUnderline: true}}
          {...rest}
        />
        <Box className={'aurAdhocSearch-textArea-button-container'}>
          {isEmpty(selectedRelationshipGroupArray) ? (
            <Button
              data-testid={'addRelationshipButton'}
              className={'aurAdhocSearch-button'}
              startIcon={<AurIcon icon={IconNames.Add} />}
              onClick={onPressAddRelationshipGroup}
              size={'medium'}
            >
              <Typography className={'aurAdhocSearch-buttonText'}>
                {t('indexSearchPage.aurAdhocSearchControl.addaRelationalGroup')}
              </Typography>
            </Button>
          ) : (
            <Box
              className={'aurAdhocSearch-relationshipGroup-button-container'}
            >
              <Typography
                component="span"
                className="aurAdhocSearch-relationshipGroup-text"
              >
                {`${
                  selectedRelationshipGroupArray.length > 1
                    ? t(
                        'indexSearchPage.aurAdhocSearchControl.relationalGroup_plural'
                      )
                    : t('indexSearchPage.aurAdhocSearchControl.relationalGroup')
                }: `}
              </Typography>
              <Button
                data-testid={'addRelationshipButtonWithGroup'}
                className={'aurAdhocSearch-relationshipGroup-button'}
                onClick={onPressAddRelationshipGroup}
                size={'medium'}
              >
                <TruncatedList
                  key={containerWidth}
                  renderTruncator={(props: {hiddenItemsCount: number}) =>
                    props.hiddenItemsCount > 0 ? (
                      <Box className="aurAdhocSearch-relationshipGroup-moreText-container">
                        <Typography className="aurAdhocSearch-relationshipGroup-moreText">
                          {`+${props.hiddenItemsCount} ${t(
                            'indexSearchPage.aurAdhocSearchControl.more'
                          )}`}
                        </Typography>
                      </Box>
                    ) : null
                  }
                  alwaysShowTruncator
                >
                  {selectedRelationshipGroupArray.map((item, index) => (
                    <Typography
                      key={index}
                      ref={index === 0 ? typographyRef : undefined}
                      noWrap
                      className={
                        'typo-text buttonMedium_size aurAdhocSearch-relationshipGroup-buttonText'
                      }
                      style={{display: 'inline', whiteSpace: 'nowrap'}}
                    >
                      {item}
                      {index < selectedRelationshipGroupArray.length - 1 &&
                        ', '}
                    </Typography>
                  ))}
                </TruncatedList>
              </Button>
            </Box>
          )}

          <Grid container className={'query-button-container'}>
            <Grid item>
              <AurButton
                dataTestId={'clearAllButton'}
                classNames={'typo-text buttonLarge_size clear-all-button'}
                type={'exit'}
                size={'Medium'}
                label={t('indexSearchPage.clearAll')}
                onClick={onClickClearAllButton}
                disabled={clearAllDisabled}
              />
            </Grid>
            <Grid item>
              <AurButton
                dataTestId={'runSearchButton'}
                classNames={'typo-text buttonLarge_size run-search-button'}
                type={'continue'}
                size={'Medium'}
                label={t('indexSearchPage.runSearch')}
                disabled={runDisabled}
                onClick={onClickRunSearch}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
});

export default AurAdhocSearchTextArea;
